import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import VoiceDictationInput from '../components/VoiceDictationInput';
import SignatureInput from '../components/SignatureInput';
import { generateIVTherapyPDF, downloadPDF, printPDF } from '../utils/pdfExport';
import { cachePdfPatients } from '../utils/fillable';
import { FileText, Save, ArrowLeft, Download, Printer, Plus, Trash2, User, Syringe, GraduationCap, ClipboardList } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IVTherapyForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const formId = searchParams.get('formId');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState({
    patientId: patientId || '',
    patientName: '',
    medicalRecordNumber: '',
    diagnosis: '',
    allergies: '',
    certificationPeriod: '',
    nurseSupervisor: '',
    // Therapy Device
    therapyDevice: '',
    infusionDeviceType: '',
    dmeVendor: '',
    // Nursing Orders
    infectionFreeAccess: true,
    venipunctureFrequency: '',
    dressingChangeFrequency: '',
    changeTubingFrequency: '',
    maintainPatent: true,
    // IV Fluids
    ivFluids: [],
    hepLockIrrigate: '',
    hepLockUnitsPerCC: '',
    hepLockCC: '',
    hepLockFrequency: '',
    asepticAdministration: true,
    // Patient Education
    ptDemonstratesAseptic: false,
    ptDemonstratesAdministration: false,
    ptVerbalizesSideEffects: false,
    ptVerbalizesComplications: false,
    ptIdentifiesResources: false,
    ptDemonstratesMonitoring: false,
    ptDemonstratesCapChange: false,
    ptDemonstratesDressingChange: false,
    educationOther: '',
    // Signatures (comprehensive - typed, voice recording, handwritten)
    patientSignature: { typed: '', audioURL: null, canvasDataURL: null },
    rnSignature: { typed: '', audioURL: null, canvasDataURL: null }
  });

  useEffect(() => {
    fetchPatients();
    if (formId) fetchForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients`, { headers: { 'Authorization': `Bearer ${token}` }});
      const data = await response.json();
      if (data.success) {
        setPatients(data.patients); cachePdfPatients(data.patients);
        if (patientId) {
          const patient = data.patients.find(p => p.id === patientId);
          if (patient) setFormData(prev => ({ ...prev, patientName: `${patient.firstName} ${patient.lastName}` }));
        }
      }
    } catch (error) { console.error('Error:', error); }
  };

  const fetchForm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/iv-therapy/${formId}`, { headers: { 'Authorization': `Bearer ${token}` }});
      const data = await response.json();
      if (data.success) setFormData(data.form);
    } catch (error) { toast.error('Failed to load form'); }
    finally { setLoading(false); }
  };

  const handlePatientChange = (e) => {
    const patient = patients.find(p => p.id === e.target.value);
    if (patient) setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: `${patient.firstName} ${patient.lastName}` }));
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const addIVFluid = () => {
    setFormData(prev => ({
      ...prev,
      ivFluids: [...prev.ivFluids, {
        drugSolution: '',
        diluentAmtType: '',
        additivesAmtType: '',
        frequency: '',
        rateOfInfusion: '',
        duration: ''
      }]
    }));
  };

  const updateIVFluid = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ivFluids: prev.ivFluids.map((fluid, i) => i === index ? { ...fluid, [field]: value } : fluid)
    }));
  };

  const removeIVFluid = (index) => {
    setFormData(prev => ({
      ...prev,
      ivFluids: prev.ivFluids.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.patientId) { toast.error('Please select a patient'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = formId ? `${API_URL}/api/forms/iv-therapy/${formId}` : `${API_URL}/api/forms/iv-therapy`;
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated' : 'Form created');
        if (!formId && data.form) navigate(`/dashboard/forms/iv-therapy?formId=${data.form.id}`);
      }
    } catch (error) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f39c12]"></div></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard/documentation')} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Syringe className="w-6 h-6 text-[#f39c12]" />IV Therapy Administration Sheet
                </h1>
                <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                  Intravenous Therapy Orders and Documentation
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                const doc = generateIVTherapyPDF(formData);
                downloadPDF(doc, `iv-therapy-${formData.patientName || 'form'}.pdf`);
              }} data-testid="export-pdf-btn"><Download className="w-4 h-4 mr-2" />PDF</Button>
              <Button variant="outline" onClick={() => {
                const doc = generateIVTherapyPDF(formData);
                printPDF(doc);
              }} data-testid="print-pdf-btn"><Printer className="w-4 h-4 mr-2" />Print</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22] text-white">
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Patient Info */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />Patient Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Patient *</Label>
              <select value={formData.patientId} onChange={handlePatientChange}
                className="w-full mt-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#f39c12]">
                <option value="">Select patient</option>
                {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
              </select>
            </div>
            <div>
              <Label>Medical Record #</Label>
              <Input value={formData.medicalRecordNumber} onChange={(e) => handleChange('medicalRecordNumber', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Certification Period</Label>
              <Input value={formData.certificationPeriod} onChange={(e) => handleChange('certificationPeriod', e.target.value)} className="mt-1" placeholder="e.g., 01/01/2024 - 03/01/2024" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Diagnosis</Label>
              <Input value={formData.diagnosis} onChange={(e) => handleChange('diagnosis', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Allergies</Label>
              <Input value={formData.allergies} onChange={(e) => handleChange('allergies', e.target.value)} className="mt-1" placeholder="List all known allergies" />
            </div>
          </div>
        </div>

        {/* Therapy Device */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Syringe className="w-5 h-5 text-purple-600" />Therapy Device
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Therapy Device Type</Label>
              <select value={formData.therapyDevice} onChange={(e) => handleChange('therapyDevice', e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#f39c12]">
                <option value="">Select device</option>
                <option value="Peripheral">Peripheral IV</option>
                <option value="PICC">PICC Line</option>
                <option value="Central">Central Line</option>
                <option value="Port">Implanted Port</option>
                <option value="Midline">Midline Catheter</option>
              </select>
            </div>
            <div>
              <Label>Infusion Device Type</Label>
              <select value={formData.infusionDeviceType} onChange={(e) => handleChange('infusionDeviceType', e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#f39c12]">
                <option value="">Select type</option>
                <option value="Gravity">Gravity</option>
                <option value="Pump">Electronic Pump</option>
                <option value="Syringe">Syringe Pump</option>
                <option value="Elastomeric">Elastomeric</option>
              </select>
            </div>
            <div>
              <Label>DME Vendor</Label>
              <Input value={formData.dmeVendor} onChange={(e) => handleChange('dmeVendor', e.target.value)} className="mt-1" />
            </div>
          </div>
        </div>

        {/* Nursing Orders */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-green-600" />Nursing Orders
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Venipuncture Frequency</Label>
              <Input value={formData.venipunctureFrequency} onChange={(e) => handleChange('venipunctureFrequency', e.target.value)} 
                className="mt-1" placeholder="e.g., Weekly PRN" />
            </div>
            <div>
              <Label>Dressing Change Frequency</Label>
              <Input value={formData.dressingChangeFrequency} onChange={(e) => handleChange('dressingChangeFrequency', e.target.value)} 
                className="mt-1" placeholder="e.g., Q 7 days" />
            </div>
            <div>
              <Label>Change Tubing Frequency</Label>
              <Input value={formData.changeTubingFrequency} onChange={(e) => handleChange('changeTubingFrequency', e.target.value)} 
                className="mt-1" placeholder="e.g., Q 72 hours" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <Label className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <Checkbox checked={formData.infectionFreeAccess} onCheckedChange={(c) => handleChange('infectionFreeAccess', c)} />
              Assess for infection-free access site
            </Label>
            <Label className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <Checkbox checked={formData.maintainPatent} onCheckedChange={(c) => handleChange('maintainPatent', c)} />
              Maintain patent catheter
            </Label>
            <Label className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <Checkbox checked={formData.asepticAdministration} onCheckedChange={(c) => handleChange('asepticAdministration', c)} />
              Aseptic administration technique
            </Label>
          </div>
        </div>

        {/* IV Fluids Table */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Syringe className="w-5 h-5 text-blue-600" />IV Fluids / Medications
            </h2>
            <Button onClick={addIVFluid} className="bg-green-600 hover:bg-green-700 text-white">
              <Plus className="w-4 h-4 mr-2" />Add Medication
            </Button>
          </div>

          {formData.ivFluids.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Syringe className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No IV medications added</p>
              <p className="text-sm">Click "Add Medication" to add an IV order</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border p-2 text-left text-sm font-medium">Drug/Solution</th>
                    <th className="border p-2 text-left text-sm font-medium">Diluent</th>
                    <th className="border p-2 text-left text-sm font-medium">Additives</th>
                    <th className="border p-2 text-left text-sm font-medium">Frequency</th>
                    <th className="border p-2 text-left text-sm font-medium">Rate</th>
                    <th className="border p-2 text-left text-sm font-medium">Duration</th>
                    <th className="border p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.ivFluids.map((fluid, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border p-1">
                        <Input value={fluid.drugSolution} onChange={(e) => updateIVFluid(index, 'drugSolution', e.target.value)} 
                          className="text-sm" placeholder="e.g., Vancomycin 1g" />
                      </td>
                      <td className="border p-1">
                        <Input value={fluid.diluentAmtType} onChange={(e) => updateIVFluid(index, 'diluentAmtType', e.target.value)} 
                          className="text-sm" placeholder="e.g., NS 250ml" />
                      </td>
                      <td className="border p-1">
                        <Input value={fluid.additivesAmtType} onChange={(e) => updateIVFluid(index, 'additivesAmtType', e.target.value)} 
                          className="text-sm" placeholder="e.g., KCl 20mEq" />
                      </td>
                      <td className="border p-1">
                        <Input value={fluid.frequency} onChange={(e) => updateIVFluid(index, 'frequency', e.target.value)} 
                          className="text-sm" placeholder="e.g., Q12H" />
                      </td>
                      <td className="border p-1">
                        <Input value={fluid.rateOfInfusion} onChange={(e) => updateIVFluid(index, 'rateOfInfusion', e.target.value)} 
                          className="text-sm" placeholder="e.g., 125ml/hr" />
                      </td>
                      <td className="border p-1">
                        <Input value={fluid.duration} onChange={(e) => updateIVFluid(index, 'duration', e.target.value)} 
                          className="text-sm" placeholder="e.g., 2 hours" />
                      </td>
                      <td className="border p-1 text-center">
                        <Button variant="ghost" size="sm" onClick={() => removeIVFluid(index)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Hep Lock */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium mb-3">Heparin/Saline Lock Flush</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm">Irrigate with</Label>
                <Input value={formData.hepLockIrrigate} onChange={(e) => handleChange('hepLockIrrigate', e.target.value)} 
                  className="mt-1 text-sm" placeholder="Heparin/Saline" />
              </div>
              <div>
                <Label className="text-sm">Units/cc</Label>
                <Input value={formData.hepLockUnitsPerCC} onChange={(e) => handleChange('hepLockUnitsPerCC', e.target.value)} 
                  className="mt-1 text-sm" placeholder="e.g., 10" />
              </div>
              <div>
                <Label className="text-sm">cc</Label>
                <Input value={formData.hepLockCC} onChange={(e) => handleChange('hepLockCC', e.target.value)} 
                  className="mt-1 text-sm" placeholder="e.g., 3" />
              </div>
              <div>
                <Label className="text-sm">Frequency</Label>
                <Input value={formData.hepLockFrequency} onChange={(e) => handleChange('hepLockFrequency', e.target.value)} 
                  className="mt-1 text-sm" placeholder="e.g., Q shift" />
              </div>
            </div>
          </div>
        </div>

        {/* Patient Education */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-indigo-600" />Patient/Caregiver Education
          </h2>
          <p className="text-sm text-gray-600 mb-4">Patient/Caregiver will be able to:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { field: 'ptDemonstratesAseptic', label: 'Demonstrate aseptic technique' },
              { field: 'ptDemonstratesAdministration', label: 'Demonstrate proper medication administration' },
              { field: 'ptVerbalizesSideEffects', label: 'Verbalize side effects of medication' },
              { field: 'ptVerbalizesComplications', label: 'Verbalize signs/symptoms of complications' },
              { field: 'ptIdentifiesResources', label: 'Identify resources in an emergency' },
              { field: 'ptDemonstratesMonitoring', label: 'Demonstrate monitoring of infusion' },
              { field: 'ptDemonstratesCapChange', label: 'Demonstrate needle-less cap change' },
              { field: 'ptDemonstratesDressingChange', label: 'Demonstrate dressing change' }
            ].map(({ field, label }) => (
              <Label key={field} className="flex items-center gap-2 p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100">
                <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                {label}
              </Label>
            ))}
          </div>
          <div className="mt-4">
            <Label>Other Education Goals</Label>
            <Input value={formData.educationOther} onChange={(e) => handleChange('educationOther', e.target.value)} 
              className="mt-1" placeholder="Additional education goals..." />
          </div>
        </div>

        {/* Signatures - Comprehensive with Type/Voice Recording/Handwritten */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Signatures</h2>
          <p className="text-sm text-gray-600 mb-4">Choose your preferred signature method: Type, Voice Recording, or Handwritten</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SignatureInput 
              label="Patient/Caregiver Signature" 
              value={formData.patientSignature} 
              onChange={(value) => handleChange('patientSignature', value)} 
            />
            <SignatureInput 
              label="RN Signature" 
              value={formData.rnSignature} 
              onChange={(value) => handleChange('rnSignature', value)} 
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-8">
          <Button variant="outline" onClick={() => navigate('/dashboard/documentation')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22] text-white">
            {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save Form</>}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default IVTherapyForm;
